import React from "react";

/**
 * Margin Lens identity marks.
 *
 * Traced from the operator-supplied logo (11 Aug 2026), replacing the NettBack
 * three-column mark when that name was rejected at ASIC.
 *
 * Geometry, in a 48x48 box, symmetric about x=24:
 *   - two solid blocks, 16 wide x 30 tall, at x=0 and x=32, from y=9 to y=39
 *   - a 2.5-wide vertical rule on the centre line, split into two segments
 *     (y=0..20.5 and y=27.5..48) leaving a 7-unit gap
 *   - an 11-wide accent crossbar sitting in that gap, centred on y=24
 *
 * The reading is a lens: two apertures either side of a focal axis, with the
 * accent marking the point of focus. The vertical rule deliberately overshoots
 * the blocks top and bottom - it is the axis the blocks are measured against,
 * which is the whole idea of the name.
 *
 * Colour comes from the design system, not from the source image. The supplied
 * artwork's orange reads darker than the live accent; the operator's decision
 * (11 Aug) was to keep the v2 tokens so the mark matches every other accent on
 * the site. Changing INK/ACCENT below changes every lockup here, and any palette
 * change needs contrast re-measured before it ships.
 */
const INK = "#171717";
const ACCENT = "#E88A51";

// Every dimension of the mark, in one place. The same numbers are duplicated
// by hand in exactly two other files that cannot import JSX - see the note at
// the foot of this file - so a change here is a change in three places.
const BLOCK = { w: 16, h: 30, y: 9, leftX: 0, rightX: 32 };
const RULE = { w: 2.5, x: 22.75, topH: 20.5, bottomY: 27.5, bottomH: 20.5 };
const CROSSBAR = { w: 11, x: 18.5, y: 22.75, h: 2.5 };

/**
 * Icon only. `tone` picks the colourway:
 *   "default"  ink bars + accent chevron   (light surfaces)
 *   "reversed" white bars + accent chevron (ink surfaces)
 *   "accent"   entirely accent            (badges, favicon on dark)
 */
export function MarkIcon({ size = 28, tone = "default", className = "", interactive = false }) {
  const bar = tone === "reversed" ? "#FFFFFF" : tone === "accent" ? ACCENT : INK;
  const lower = tone === "accent" ? ACCENT : bar;

  // The v1 wordmark's three bars scaled on hover. That behaviour is kept, but
  // tied to the mark's own meaning: the bars grow from their baseline and the
  // chevron lifts, so hovering reads as the chart moving up rather than as a
  // generic scale. Staggered delays keep it from snapping as one block.
  //
  // SVG children need `transform-box: fill-box` before a percentage
  // transform-origin resolves against the shape instead of the viewport.
  const anim = (origin, delay) =>
    interactive
      ? {
          transformBox: "fill-box",
          transformOrigin: origin,
          transition: `transform 340ms cubic-bezier(.2,.7,.3,1) ${delay}ms`,
        }
      : undefined;

  const g = (cls) => (interactive ? cls : "");

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      className={className}
      role="img"
      aria-label="Margin Lens"
    >
      <rect
        x={BLOCK.leftX} y={BLOCK.y} width={BLOCK.w} height={BLOCK.h} fill={bar}
        className={g("group-hover:scale-y-[1.10]")} style={anim("bottom", 0)}
      />
      <rect
        x={BLOCK.rightX} y={BLOCK.y} width={BLOCK.w} height={BLOCK.h} fill={bar}
        className={g("group-hover:scale-y-[1.10]")} style={anim("bottom", 80)}
      />
      <rect x={RULE.x} y="0" width={RULE.w} height={RULE.topH} fill={lower} />
      <rect x={RULE.x} y={RULE.bottomY} width={RULE.w} height={RULE.bottomH} fill={lower} />
      <rect
        x={CROSSBAR.x} y={CROSSBAR.y} width={CROSSBAR.w} height={CROSSBAR.h} fill={ACCENT}
        className={g("group-hover:scale-x-[1.18]")} style={anim("center", 40)}
      />
    </svg>
  );
}

/**
 * Horizontal lockup — icon beside the wordmark. This is the primary mark and
 * the one that sits at the top of the homepage and in the nav.
 *
 * The wordmark is live text rather than outlines so it stays selectable and
 * accessible. The pack calls for Inter Medium; `font-sans-alt` (Plus Jakarta
 * Sans) is the closest grotesque already loaded, so the stack prefers Inter
 * where the system has it and falls back to Jakarta.
 */
export function LogoHorizontal({ size = 28, tone = "default", className = "", interactive = false }) {
  const ink = tone === "reversed" ? "#FFFFFF" : INK;

  return (
    <span className={`inline-flex items-center gap-2.5 ${interactive ? "group" : ""} ${className}`}>
      <MarkIcon size={size} tone={tone} interactive={interactive} />
      <span
        className="font-semibold tracking-tight leading-none whitespace-nowrap"
        style={{
          fontFamily: "Inter, 'Plus Jakarta Sans', system-ui, sans-serif",
          fontSize: size * 0.86,
          color: ink,
        }}
      >
        Margin Lens
      </span>
    </span>
  );
}

/** Stacked lockup — icon above the wordmark. Narrow columns, PDF cover. */
export function LogoStacked({ size = 44, tone = "default", className = "" }) {
  const ink = tone === "reversed" ? "#FFFFFF" : INK;

  return (
    <span className={`inline-flex flex-col items-center gap-2 ${className}`}>
      <MarkIcon size={size} tone={tone} />
      <span
        className="font-semibold tracking-tight leading-none"
        style={{
          fontFamily: "Inter, 'Plus Jakarta Sans', system-ui, sans-serif",
          fontSize: size * 0.4,
          color: ink,
        }}
      >
        Margin Lens
      </span>
    </span>
  );
}

export default LogoHorizontal;
